import React, { useEffect, useId, useState } from "react";
import { signOut } from "../../utils/signOut";
import { useForm } from "react-hook-form";
import Cookies from "js-cookie";
import { API_DETAILS, COOKIE_KEYS } from "../../App";
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { toast } from '../../utils/toast';
import { useSubmitGuard } from '../../utils/useSubmitGuard';
import { errorMessage } from '../../utils/errorMessage';
import imageCompression from 'browser-image-compression';
import Loader from "../../utils/loader";
import SymptomCalculator from './SymptomCalculator';

/*
 * InfoField and SectionHeader live at module scope. Declared inside
 * PatientProfile they were new component types on every render, so each of the
 * eleven fields was torn down and rebuilt whenever anything on the page
 * changed — losing focus mid-edit.
 */
const InfoField = ({ label, type, readOnly = true, registration, error }) => {
  const [isFocused, setIsFocused] = useState(false);
  // Ties the visible label, and any error, to the input for screen readers.
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const { onBlur: rhfOnBlur, ...restRegistration } = registration || {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <label htmlFor={inputId} style={{ fontSize: '0.875rem', fontWeight: 500, lineHeight: 1 }}>
        {label}
      </label>
      <input
        id={inputId}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        type={type}
        readOnly={readOnly}
        {...restRegistration}
        onFocus={() => setIsFocused(true)}
        onBlur={(e) => {
          setIsFocused(false);
          if (rhfOnBlur) rhfOnBlur(e);
        }}
        style={{
          display: 'flex',
          height: '36px',
          width: '100%',
          borderRadius: '6px',
          border: readOnly
            ? '1px solid var(--input, #e2e8f0)'
            : (error ? '1px solid var(--youro-error)' : '1px solid transparent'),
          backgroundColor: readOnly ? 'var(--youro-sand)' : '#ffffff',
          padding: '4px 12px',
          fontSize: '0.875rem',
          color: 'var(--foreground, #000)',
          cursor: readOnly ? 'default' : 'text',
          boxSizing: 'border-box',
          boxShadow: isFocused ? "0 0 0 3px rgba(143, 168, 122, 0.6)" : "none",
          transition: 'all 0.2s ease-in-out'
        }}
      />

      {error && (
        <span id={errorId} className="error-text" role="alert">
          {error.message || "This field is invalid"}
        </span>
      )}
    </div>
  );
};

const SectionHeader = ({ title, isEditing, onEdit, onSave }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
    <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>{title}</h3>

    {isEditing ? (
      <button
        onClick={onSave}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          height: '36px', padding: '0 16px', borderRadius: '6px', fontSize: '0.875rem', fontWeight: 500,
          backgroundColor: 'var(--youro-coral)', border: '1px solid var(--youro-coral)', color: '#ffffff',
          cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s'
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"></path>
          <path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7"></path>
          <path d="M7 3v4a1 1 0 0 0 1 1h7"></path>
        </svg>
        Save
      </button>
    ) : (
      // EDIT BUTTON
      <button
        onClick={onEdit}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          height: '36px', padding: '0 16px', borderRadius: '6px', fontSize: '0.875rem', fontWeight: 500,
          backgroundColor: 'transparent', border: '1px solid var(--youro-sage)', color: 'var(--youro-sage)',
          cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s'
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
          <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"></path>
        </svg>
        Edit
      </button>
    )}
  </div>
);

const PatientProfile = (props) => {
  const navigate = useNavigate();
  const [editingSection, setEditingSection] = useState(null);

  // The placeholder formData state and its handleChange went with the card
  // form. Every field still on this screen is driven by react-hook-form's
  // register(), reading the patient's real record rather than "Alex Martinez".

  // The one shared sign-out: ends the session on the server too. See utils/signOut.
  const handleLogout = () => signOut();

  const [isPopupVisible, setPopupVisible] = useState(false);
  const [insurance, setInsurance] = useState("yes");
  const [viewVal, setViewVal] = useState(0);

  const showPopup = (data) => {
    setPopupVisible(true);
  };

  const hidePopup = () => setPopupVisible(false);

  const handleYes = () => hidePopup();
  const handleNo = () => hidePopup();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm();

  useEffect(() => {
    const loadInitialData = async () => {
      setActiveLoader(true);
      try {
        await Promise.all([fetchProfileData(), get_profile_pic()]);
      } catch (error) {
      } finally {
        setActiveLoader(false);
      }
    };

    loadInitialData();
  }, []);

  const doctor_id = Cookies.get(COOKIE_KEYS.userId);

  const [imagePreview, setImagePreview] = useState(null);
  const [toggle_image, setToggle_image] = useState(false);
  const [activeLoader, setActiveLoader] = useState(false);
  const [, guard] = useSubmitGuard();
  const [open, setOpen] = useState(false);
  const uId = Cookies.get(COOKIE_KEYS.userId);
  const [fetchSymptomScore, setFetchSymptomScore] = useState(false);
  const [selectedDiagId, setSelectedDiagId] = useState('');
  const [selectedApptId, setSelectedApptId] = useState(null);
  const [chooseDiagnosis, setChooseDiagnosis] = useState(false);
  const [selectedDiagName, setSelectedDiagName] = useState('');
  const [isIntakeForm, setIsIntakeForm] = useState(false);

  // ✅ PROMO CODE UI STATE

  const toggle_profile_image = () => setToggle_image(!toggle_image);

  const get_profile_pic = async () => {
    const doctor_id_1 = Cookies.get(COOKIE_KEYS.userId);
    const get_url = API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + `/getDp/${doctor_id_1}`;

    try {
      const response = await axios.get(get_url, { responseType: 'arraybuffer' });

      if (response.status === 200) {
        const arrayBuffer = new Uint8Array(response.data);
        if (arrayBuffer.length !== 0) {
          const base64Image = btoa(
            new Uint8Array(arrayBuffer).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              ''
            )
          );
          const contentType = response.headers['content-type'];
          setImagePreview(`data:${contentType};base64,${base64Image}`);
        }
      }
    } catch (err) {
    }
  };

  const handleImageChange = async (e) => {
    const doctor_id_2 = Cookies.get(COOKIE_KEYS.userId);

    const file = e.target.files[0];
    if (file) {
      const options = {
        maxSizeMB: 5,
        maxWidthOrHeight: 1920,
        useWebWorker: true
      };
      const compressedFile = await imageCompression(file, options);
      setValue('image', compressedFile);


      const requestData = {
        'userId': doctor_id_2,
        'image': compressedFile,
      };

      const url = API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + `/uploadDp`;

      const config = {
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': '*',
          'Content-Type': 'multipart/form-data',
        }
      };

      setActiveLoader(true);
      try {
        const res = await axios.post(url, requestData, config);
        await get_profile_pic();
      } catch (err) {
      } finally {
        setActiveLoader(false);
      }
    }
  };

  const fetchProfileData = async () => {
    const uIdLocal = Cookies.get(COOKIE_KEYS.userId);
    const url = API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + `/getUser/${uIdLocal}`;

    const config = {
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': '*',
        'Content-Type': 'application/json'
      }
    };

    try {
      const res = await axios.get(url, config);
      setValue("firstName", res.data.firstName);
      setValue("lastName", res.data.lastName);
      setValue("email", res.data.email);
      setValue("phone1", res.data.phone1);
      setValue("hasInsurance", res.data.hasInsurance ? "yes" : "no");
      setValue("address", res.data.address);
      setValue("city", res.data.city);
      setValue("state", res.data.state);
      setValue("zipCode", res.data.zipCode);
      setValue("dateOfBirth", res.data.dateOfBirth);
      setValue("newPassword", '');
      setInitEmail(res.data.email);
    } catch (err) {
    }
  };

  const [initEmail, setInitEmail] = useState('');

  const updateProfileData = guard(async (data) => {
    const url = API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + `/provider/updateProfile`;
    data.hasInsurance = (data.hasInsurance === "yes" ? true : false);

    let temp = { ...data };
    if (temp.newPassword === '') {
      delete temp.password;
    } else {
      temp.password = temp.newPassword;
    }

    temp.userId = Cookies.get(COOKIE_KEYS.userId);
    delete temp.newPassword;
    delete temp.confirmPassword;
    // Only sent when it is actually needed, so an ordinary save of an address or a
    // phone number does not carry a password with it.
    if (!temp.password && initEmail === temp.email) {
      delete temp.currentPassword;
    }

    // Whether the email is actually being changed has to be captured before the
    // field is removed: the checks after the save compared initEmail against a
    // deleted (undefined) temp.email, so every ordinary save reported that the
    // email had failed to change.
    const emailChanged = initEmail !== temp.email;
    if (!emailChanged) {
      delete temp.email;
    }

    const config = {
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': '*',
        'Content-Type': 'application/json'
      }
    };

    setActiveLoader(true);
    try {
      const res = await axios.put(url, temp, config);
      setEditingSection(null);
      if (emailChanged) {
        if (res.data.email === initEmail) {
          setValue("email", initEmail);
          toast.error("Your other changes are saved, but your email wasn't changed. That address may already be in use, so try a different one.");
        } else {
          // A taken address is refused by keeping the old one, which is the branch above.
          toast.success("Your changes are saved. Use your new email the next time you sign in.");
        }
      } else {
        toast.success("Your changes are saved.");
      }
    } catch (err) {
      toast.error(errorMessage(err, "We couldn't save your changes. Check the details and try again."));
    } finally {
      setActiveLoader(false);
    }
  });

  const handleFillSymptomScoreClick = (diagName, diagId, apptId) => {
    setSelectedDiagId(diagId.toString());
    setSelectedDiagName(diagName);
    setChooseDiagnosis(true);
    setSelectedApptId(apptId);
    if (diagName === "Intake form") {
      setIsIntakeForm(true);
    }
    if (diagId || diagId === 0) setOpen(true);
  };

  // The promo code form moved to the Billing view along with the membership
  // and payment history, so its handler lives there too.



  return (
    <div className='d-profile1' style={{ display: 'flex', width: '100%' }}>
      <Loader active={activeLoader} />
      {/*
      <div className='profile-side-bar'>
        <DoctorSideBar data={'doctor-profile'} />
      </div>
      */}

      <div className="d-container">
        <div className='profile-column'>
          <div style={{ width: "100%", margin: "1.5% 2%" }}>

            <div style={{ maxWidth: '896px', margin: '0 auto', padding: '32px', color: 'var(--foreground)' }}>

              {/* Page Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
                <button
                  onClick={() => props.openProfileMenu?.()}
                  style={{
                  padding: '8px', borderRadius: '8px', border: '1px solid var(--border)',
                  backgroundColor: 'var(--card)', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center'
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>
                  </svg>
                </button>
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Personal Information</h2>
              </div>

              {/* Basic Information Card */}
              <div style={{ padding: '24px', borderRadius: '8px', marginBottom: '24px', backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}>
                <SectionHeader
                  title="Basic Information"
                  isEditing={editingSection === 'basic'}
                  onEdit={() => setEditingSection('basic')}
                  onSave={handleSubmit(updateProfileData)}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                  <InfoField
                    label="First Name"
                    type="text"
                    readOnly={editingSection !== 'basic'}
                    registration={register("firstName", {
                      required: "First Name is required",
                      maxLength: { value: 32, message: "First name cannot exceed 32 characters" }
                    })}
                    error={errors?.firstName}
                  />
                  {/* The account has always had a last name — it is loaded into the
                      form and sent back on save — but there was no field for it, so
                      a patient could not correct a misspelled surname. */}
                  <InfoField
                    label="Last Name"
                    type="text"
                    readOnly={editingSection !== 'basic'}
                    registration={register("lastName", {
                      required: "Last Name is required",
                      maxLength: { value: 32, message: "Last name cannot exceed 32 characters" }
                    })}
                    error={errors?.lastName}
                  />
                  <InfoField
                    label="Date of Birth"
                    type="date"
                    readOnly={editingSection !== 'basic'}
                    registration={register("dateOfBirth", {
                      required: "Date of Birth is required",
                      max: {
                        value: new Date().toISOString().split("T")[0],
                        message: "Date of Birth cannot be in the future",
                      }
                    })}
                    error={errors?.dateOfBirth}
                  />
                  <InfoField
                    label="Phone Number"
                    type="tel"
                    readOnly={editingSection !== 'basic'}
                    registration={register("phone1", {
                      required: "Phone Number is required",
                      maxLength: {
                        value: 10,
                        message: "Phone Number cannot exceed 10 digits"
                      },
                      pattern: {
                        value: /^[0-9]+$/,
                        message: "Please enter only numbers"
                      }
                    })}
                    error={errors?.phone1}
                  />
                  <InfoField
                    label="Email Address"
                    type="email"
                    readOnly={editingSection !== 'basic'}
                    registration={register("email", {
                      required: "Email Address is required",
                      maxLength: {
                        value: 32,
                        message: "Email cannot exceed 32 characters"
                      },
                      pattern: {
                        value: /([-!#-'*+/-9=?A-Z^-~]+(\.[-!#-'*+/-9=?A-Z^-~]+)*|"([]!#-[^-~ \t]|(\\[\t -~]))+")@[0-9A-Za-z]([0-9A-Za-z-]{0,61}[0-9A-Za-z])?(\.[0-9A-Za-z]([0-9A-Za-z-]{0,61}[0-9A-Za-z])?)+/,
                        message: "Please enter valid email address"
                      }
                    })}
                    error={errors?.email}
                  />
                </div>
              </div>

              {/* Address Card */}
              <div style={{ padding: '24px', borderRadius: '8px', marginBottom: '24px', backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}>
                <SectionHeader
                  title="Address"
                  isEditing={editingSection === 'address'}
                  onEdit={() => setEditingSection('address')}
                  onSave={handleSubmit(updateProfileData)}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <InfoField
                    label="Street Address"
                    type="text"
                    readOnly={editingSection !== 'address'}
                    registration={register("address", {
                      required: "Street Address is required",
                      maxLength: {
                        value: 50,
                        message: "Address cannot exceed 50 characters"
                      }
                    })}
                    error={errors?.address}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>
                    <InfoField
                      label="City"
                      type="text"
                      readOnly={editingSection !== 'address'}
                      registration={register("city", {
                        required: "City is required",
                        maxLength: {
                          value: 32,
                          message: "City cannot exceed 50 characters"
                        }
                      })}
                      error={errors?.city}
                    />
                    <InfoField
                      label="State"
                      type="text"
                      readOnly={editingSection !== 'address'}
                      registration={register("state", {
                        required: "State is required",
                        maxLength: {
                          value: 32,
                          message: "State cannot exceed 50 characters"
                        }
                      })}
                      error={errors?.state}
                    />
                    <InfoField
                      label="ZIP Code"
                      type="text"
                      /* Tells mobile phones to open the number pad */
                      inputMode="numeric"
                      readOnly={editingSection !== 'address'}
                      registration={register("zipCode", {
                        required: "Zipcode is required",
                        maxLength: {
                          value: 50,
                          message: "Zipcode cannot exceed 50 characters"
                        },
                        pattern: {
                          value: /^\d{5}(-\d{4})?$/,
                          message: "Please enter a valid 5-digit ZIP code"
                        }
                      })}
                      error={errors?.zipCode}
                    />
                  </div>
                </div>
              </div>

              {/* Credentials */}
              <div style={{ padding: '24px', borderRadius: '8px', marginBottom: '24px', backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}>
                <SectionHeader
                  title="Credentials"
                  isEditing={editingSection === 'credentials'}
                  onEdit={() => setEditingSection('credentials')}
                  onSave={handleSubmit(updateProfileData)}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                  {/* The server refuses a new email address or password without
                      this. A session token on its own is not enough to take an
                      account over, which is what it used to be. */}
                  <InfoField
                    label="Current Password"
                    type="password"
                    readOnly={editingSection !== 'credentials'}
                    registration={register("currentPassword", {
                      validate: (value, formValues) => {
                        const changingEmail = formValues.email !== initEmail;
                        if ((formValues.newPassword || changingEmail) && !value) {
                          return "Enter your current password to change your email or password";
                        }
                        return true;
                      }
                    })}
                    error={errors?.currentPassword}
                  />
                  <InfoField
                    label="New Password"
                    type="password"
                    readOnly={editingSection !== 'credentials'}
                    registration={register("newPassword", {
                      required: false,
                      maxLength: {
                        value: 32,
                        message: "Password cannot exceed 32 characters"
                      },
                      minLength: {
                        value: 8,
                        message: "Password should be at least 8 characters"
                      }
                    })}
                    error={errors?.newPassword}
                  />
                  <InfoField
                    label="Confirm Password"
                    type="password"
                    readOnly={editingSection !== 'credentials'}
                    registration={register("confirmPassword", {
                      validate: (value, formValues) => {
                        if (formValues.newPassword && !value) {
                          return "Please confirm your password";
                        }
                        if (formValues.newPassword !== value) {
                          return "Passwords do not match";
                        }
                        return true;
                      }
                    })}
                    error={errors?.confirmPassword}
                  />
                </div>
              </div>

              {/* Membership, payment history and promo codes now live on the
                  Billing view. Keeping them here made Billing and Personal
                  Information the same screen, which is not what either name
                  promises. There is deliberately no card-details form on either:
                  cards are entered on Stripe's own Checkout page and stored by
                  Stripe, so Youro never receives a card number. */}
            </div>
          </div>
        </div>
      </div>

      {open && (
        <SymptomCalculator
          uId={uId}
          open={open}
          setOpen={setOpen}
          fetchSymptomScore={setFetchSymptomScore}
          diagId={selectedDiagId}
          apptId={selectedApptId}
          setDiagId={setSelectedDiagId}
          choosenDiagnosis={chooseDiagnosis}
          diagName={selectedDiagName}
          isIntakeForm={isIntakeForm}
          editIntake={true}
          fromLandingPage={false}
        />
      )}
    </div>
  );
};

export default PatientProfile;
